import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ChevronDown, Copy, CheckCircle2, XCircle, RefreshCw,
  Clock, ArrowUpRight, Wallet, AlertCircle, X
} from 'lucide-react'
import cryptoList from '../constants/cryptoList'
import './styles/Withdrawal.scss'
import { initiateCryptoWithdrawal } from 'redux-thunk/thunk/payment.thunk'

const shortId = (id) => {
  if (!id) return '—'
  const s = String(id)
  return s.length > 20 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s
}

const formatTime = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const PCT_OPTS = [25, 50, 75, 100]

const Withdrawal = () => {
  const dispatch = useDispatch()
  const { selectedWallet: wallet, user } = useSelector(s => s.user || {})
  const { withdrawLoading } = useSelector(s => s.cryptoSlice || {})

  const [phase, setPhase]               = useState('form')
  const [crypto, setCrypto]             = useState(null)
  const [network, setNetwork]           = useState('')
  const [address, setAddress]           = useState('')
  const [amount, setAmount]             = useState('')
  const [openCrypto, setOpenCrypto]     = useState(false)
  const [openNetwork, setOpenNetwork]   = useState(false)
  const [search, setSearch]             = useState('')
  const [submitting, setSubmitting]     = useState(false)
  const [amountError, setAmountError]   = useState('')
  const [formError, setFormError]       = useState('')
  const [lastWithdraw, setLastWithdraw] = useState(null)
  const [copiedField, setCopiedField]   = useState(null)

  const available    = parseFloat(wallet?.amount || 0)
  const currencyCode = wallet?.currency?.code?.toUpperCase() || ''

  const filteredCryptos = useMemo(() =>
    cryptoList.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.symbol.toLowerCase().includes(search.toLowerCase())
    ), [search])

  const parsedAmount = parseFloat(amount)
  const isAmountValid = !Number.isNaN(parsedAmount) && parsedAmount > 0
  const isFormValid   = crypto && network && address.trim() && isAmountValid && !amountError

  useEffect(() => { setNetwork('') }, [crypto])

  useEffect(() => {
    if (!isAmountValid) { setAmountError(''); return }
    setAmountError(parsedAmount > available ? 'Exceeds available balance.' : '')
  }, [amount, available])

  const setPercent = (pct) => {
    if (!available) return
    const val = pct === 100 ? available : parseFloat((available * pct / 100).toFixed(8))
    setAmount(String(val))
  }

  const copyField = (value, key) => {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(key)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }

  const closeDropdowns = () => { setOpenCrypto(false); setOpenNetwork(false) }

  const handleWithdraw = async () => {
    if (submitting || !isFormValid) {
      if (!isFormValid) setFormError('Please complete all fields before continuing.')
      return
    }
    setSubmitting(true)
    setFormError('')

    try {
      const action = await dispatch(initiateCryptoWithdrawal({
        amount:            parsedAmount,
        address:           address.trim(),
        crypto_currency:   crypto.symbol,
        network_fee_speed: 'standard',
        user_id:           user?.id
      }))

      if (action?.meta?.requestStatus === 'fulfilled') {
        const data = action?.payload?.data || action?.payload || {}
        setLastWithdraw({
          amount:        parsedAmount,
          symbol:        crypto.symbol,
          logo:          crypto.logo,
          network,
          address:       address.trim(),
          transactionId: data?.transactionId || data?.id || data?.reference || null,
          timestamp:     new Date()
        })
        setPhase('success')
      } else {
        throw new Error(action?.payload?.message || action?.error?.message || 'Withdrawal failed.')
      }
    } catch (err) {
      setFormError(err?.message || 'Withdrawal failed. Please try again.')
      setPhase('error')
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setPhase('form'); setFormError(''); setLastWithdraw(null); setCopiedField(null)
    setCrypto(null);  setNetwork('');   setAddress('');        setAmount('');  setSearch('')
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div className='cw-state-wrap'>
        <div className='cw-state-icon cw-state-icon--success'>
          <CheckCircle2 size={30} strokeWidth={1.5} />
        </div>
        <div className='cw-state-amount'>
          {lastWithdraw?.logo && <img src={lastWithdraw.logo} alt='' className='cw-state-coin-img' />}
          {lastWithdraw?.amount} <span>{lastWithdraw?.symbol}</span>
        </div>
        <h3>Withdrawal Submitted</h3>
        <p>Your request is queued. Funds arrive after network confirmation.</p>

        <div className='cw-receipt'>
          <div className='cw-receipt-row'>
            <span className='cw-receipt-label'>Status</span>
            <span className='cw-receipt-badge cw-receipt-badge--pending'><Clock size={10} /> Pending</span>
          </div>
          <div className='cw-receipt-row'>
            <span className='cw-receipt-label'>Asset</span>
            <span className='cw-receipt-val'>{lastWithdraw?.symbol}</span>
          </div>
          <div className='cw-receipt-row'>
            <span className='cw-receipt-label'>Network</span>
            <span className='cw-receipt-val'>{lastWithdraw?.network}</span>
          </div>
          <div className='cw-receipt-row'>
            <span className='cw-receipt-label'>Amount</span>
            <span className='cw-receipt-val'>{lastWithdraw?.amount} {lastWithdraw?.symbol}</span>
          </div>
          <div className='cw-receipt-row'>
            <span className='cw-receipt-label'>To Address</span>
            <span className='cw-receipt-id-wrap'>
              <code className='cw-receipt-id'>{shortId(lastWithdraw?.address)}</code>
              <button className='cw-receipt-copy' type='button' onClick={() => copyField(lastWithdraw?.address, 'addr')}>
                {copiedField === 'addr' ? <CheckCircle2 size={12} color='#34d399' /> : <Copy size={12} />}
              </button>
            </span>
          </div>
          {lastWithdraw?.transactionId && (
            <div className='cw-receipt-row'>
              <span className='cw-receipt-label'>Reference ID</span>
              <span className='cw-receipt-id-wrap'>
                <code className='cw-receipt-id'>{shortId(lastWithdraw.transactionId)}</code>
                <button className='cw-receipt-copy' type='button' onClick={() => copyField(lastWithdraw.transactionId, 'txid')}>
                  {copiedField === 'txid' ? <CheckCircle2 size={12} color='#34d399' /> : <Copy size={12} />}
                </button>
              </span>
            </div>
          )}
          <div className='cw-receipt-row cw-receipt-row--last'>
            <span className='cw-receipt-label'>Time</span>
            <span className='cw-receipt-val'>{formatTime(lastWithdraw?.timestamp)}</span>
          </div>
        </div>

        <button className='cw-btn-ghost' type='button' onClick={reset}>
          <RefreshCw size={13} /> Make another withdrawal
        </button>
      </div>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className='cw-state-wrap'>
        <div className='cw-state-icon cw-state-icon--error'>
          <XCircle size={30} strokeWidth={1.5} />
        </div>
        <h3>Withdrawal Failed</h3>
        <p>{formError || 'Something went wrong. Please try again.'}</p>
        <button className='cw-btn-ghost' type='button' onClick={reset}>
          <RefreshCw size={13} /> Try again
        </button>
      </div>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className='cw-wrap'>

      {/* Balance card */}
      <div className='cw-balance-card'>
        <span className='cw-balance-eyebrow'><Wallet size={11} /> Available Balance</span>
        <span className='cw-balance-figure'>
          ${available.toFixed(2)}
          {currencyCode && <span className='cw-balance-currency'>{currencyCode}</span>}
        </span>
      </div>

      {/* Currency + Network — full-width stacked for equal alignment */}
      <div className='cw-field'>
        <p className='cw-label'>Currency</p>
        <button
          className='cw-dropdown-btn'
          type='button'
          onClick={() => { setOpenCrypto(v => !v); setOpenNetwork(false) }}
        >
          {crypto ? (
            <>
              <img src={crypto.logo} alt={crypto.symbol} className='cw-coin-img' />
              <span className='cw-coin-label'>
                <strong>{crypto.symbol}</strong>
                <small>{crypto.name}</small>
              </span>
            </>
          ) : (
            <span className='cw-dropdown-placeholder'>Select cryptocurrency</span>
          )}
          <ChevronDown size={14} className={`cw-chevron ${openCrypto ? 'open' : ''}`} />
        </button>

        {openCrypto && (
          <div className='cw-dropdown-list'>
            <div className='cw-search-wrap'>
              <input
                className='cw-search'
                placeholder='Search coins…'
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
              {search && (
                <button className='cw-search-clear' type='button' onClick={() => setSearch('')}>
                  <X size={12} />
                </button>
              )}
            </div>
            <div className='cw-list-scroll'>
              {filteredCryptos.map(c => (
                <button
                  key={c.symbol}
                  className={`cw-list-item ${c.symbol === crypto?.symbol ? 'active' : ''}`}
                  type='button'
                  onClick={() => { setCrypto(c); setSearch(''); setOpenCrypto(false) }}
                >
                  <img src={c.logo} alt={c.symbol} className='cw-coin-img cw-coin-img--sm' />
                  <span className='cw-coin-label'>
                    <strong>{c.symbol}</strong>
                    <small>{c.name}</small>
                  </span>
                </button>
              ))}
              {filteredCryptos.length === 0 && (
                <p className='cw-list-empty'>No coins match "{search}"</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='cw-field'>
        <p className='cw-label'>Network</p>
        <button
          className='cw-dropdown-btn'
          type='button'
          disabled={!crypto}
          onClick={() => { setOpenNetwork(v => !v); setOpenCrypto(false) }}
        >
          {network
            ? <span className='cw-net-val'>{network}</span>
            : <span className='cw-dropdown-placeholder'>{crypto ? 'Select network' : 'Select a currency first'}</span>
          }
          {crypto && <ChevronDown size={14} className={`cw-chevron ${openNetwork ? 'open' : ''}`} />}
        </button>

        {openNetwork && crypto && (
          <div className='cw-dropdown-list'>
            <div className='cw-list-scroll'>
              {crypto.networks.map(net => (
                <button
                  key={net}
                  className={`cw-list-item ${net === network ? 'active' : ''}`}
                  type='button'
                  onClick={() => { setNetwork(net); setOpenNetwork(false) }}
                >
                  <span className='cw-net-val'>{net}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Amount */}
      <div className='cw-field' onFocus={closeDropdowns}>
        <p className='cw-label'>Amount</p>
        <div className='cw-amount-wrap'>
          <input
            className={`cw-amount-input ${amountError ? 'error' : ''}`}
            type='number'
            placeholder='0.00'
            value={amount}
            onChange={e => setAmount(e.target.value)}
            min='0'
            step='any'
          />
          {crypto && <span className='cw-amount-sym'>{crypto.symbol}</span>}
        </div>
        {amountError
          ? <p className='cw-input-error'><AlertCircle size={11} /> {amountError}</p>
          : (amount && isAmountValid && (
            <p className='cw-amount-after'>
              Remaining: <strong>{Math.max(0, available - parsedAmount).toFixed(2)} {currencyCode}</strong>
            </p>
          ))
        }
        <div className='cw-pct-row'>
          {PCT_OPTS.map(pct => (
            <button
              key={pct}
              className='cw-pct-btn'
              type='button'
              disabled={!available}
              onClick={() => setPercent(pct)}
            >
              {pct === 100 ? 'MAX' : `${pct}%`}
            </button>
          ))}
        </div>
      </div>

      {/* Address */}
      <div className='cw-field' onFocus={closeDropdowns}>
        <p className='cw-label'>Destination Address</p>
        <input
          className='cw-input'
          type='text'
          placeholder={network ? `Your ${network} wallet address` : 'Paste wallet address'}
          value={address}
          onChange={e => setAddress(e.target.value)}
        />
      </div>

      {/* Fee + warning */}
      <div className='cw-fee-row'>
        <span className='cw-fee-label'>Network fee</span>
        <span className='cw-fee-val'>Standard · ~5–30 min</span>
      </div>

      <div className='cw-info-note'>
        <AlertCircle size={13} />
        <span>Only send {crypto?.symbol || 'the selected asset'} on the correct network. Sending to the wrong network results in permanent loss.</span>
      </div>

      {formError && (
        <p className='cw-form-error'><AlertCircle size={12} /> {formError}</p>
      )}

      <button
        className='cw-submit'
        type='button'
        disabled={submitting || !isFormValid || withdrawLoading}
        onClick={handleWithdraw}
      >
        {submitting || withdrawLoading
          ? <><span className='cw-submit-spinner' /> Processing…</>
          : <><ArrowUpRight size={15} /> {amount && isAmountValid && crypto ? `Withdraw ${amount} ${crypto.symbol}` : 'Withdraw'}</>
        }
      </button>

    </div>
  )
}

export default Withdrawal
